import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Logo from 'assets/logo_full.png';
import PlaceholderImage from 'assets/logo_full.png';
import MobileBottomNav from 'components/layout/MobileBottomNav';
import SeoBreadcrumbs from 'components/seo/SeoBreadcrumbs';
import StructuredData from 'seo/StructuredData';
import { AI_META_TAGS, AI_LINK_TAGS, buildAiCitationMeta } from 'seo/aiMeta';
import { GOOGLE_META_TAGS, GOOGLE_ROBOTS_CONTENT } from 'seo/googleSeo';
import {
  buildTarotPageTitle,
  buildTarotPageDescription,
  buildTarotCanonicalUrl,
  buildTarotKeywords,
  DEFAULT_OG_IMAGE,
} from 'seo/seoConfig';
import { recordVisit } from 'storage/userStorage';
import { captureReferralFromUrl } from 'viral/viralShare';
import TarotExperience from 'components/tarot/TarotExperience';
import 'css/tokens.css';
import 'css/style.css';
import 'css/tarot.css';

export default function TarotMain() {
  const pageTitle = buildTarotPageTitle();
  const pageDescription = buildTarotPageDescription();
  const canonicalUrl = buildTarotCanonicalUrl();

  useEffect(() => {
    recordVisit();
    captureReferralFromUrl(window.location.search);
  }, []);

  return (
    <div className="hotto-app">
      <Helmet>
        <html lang="th" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={buildTarotKeywords()} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="th-th" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta name="robots" content={GOOGLE_ROBOTS_CONTENT} />
        {GOOGLE_META_TAGS.map((tag) => (
          <meta key={tag.name} name={tag.name} content={tag.content} />
        ))}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:locale" content="th_TH" />
        <meta property="og:site_name" content="Hotto" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        {AI_META_TAGS.map((tag) => (
          <meta key={tag.name} name={tag.name} content={tag.content} />
        ))}
        {AI_LINK_TAGS.map((tag) => (
          <link key={`${tag.rel}-${tag.href}`} rel={tag.rel} href={tag.href} {...(tag.type ? { type: tag.type } : {})} {...(tag.title ? { title: tag.title } : {})} />
        ))}
        {buildAiCitationMeta(canonicalUrl).map((tag) =>
          tag.name ? (
            <meta key={tag.name} name={tag.name} content={tag.content} />
          ) : (
            <meta key={tag.property} property={tag.property} content={tag.content} />
          )
        )}
      </Helmet>

      <StructuredData mode="tarot" />

      <SeoBreadcrumbs mode="tarot" />

      <header className="site-header">
        <a href="/" className="logo-link" aria-label="กลับหน้าแรก Hotto">
          <LazyLoadImage
            src={Logo}
            alt="Hotto"
            placeholdersrc={PlaceholderImage}
            effect="blur"
            width={88}
            height={88}
          />
          <span className="logo-wordmark">hotto</span>
        </a>
      </header>

      <div className="hotto-body">
        <main id="main-content" className="tarot-page">
          <TarotExperience />
        </main>
      </div>

      <footer className="site-footer" aria-label="ข้อมูลเว็บไซต์">
        <p>Hotto — ไพ่ยิปซีเลขนำโชค · หาเลขเด็ดจากฝัน · เช็คสถิติหวย 32 ปี</p>
      </footer>

      <MobileBottomNav />
    </div>
  );
}
